import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { customElement } from "../../marcel/customElement";
import { useNavigate, useSearchParams } from "react-router-dom";
import MrclText from '../../marcel/MrclText';
import MrclInputText from '../../marcel/MrclInputText';
import { formatKvalue } from "../../../utils/numberUtils";
import { FormatGroup } from "../../../types/commercial";
import LoadingCard from "../components/LoadingCard";

// Import the existing shared DeltaBadge component instead of Evolution
import DeltaBadge from "../../DeltaBadge/DeltaBadge";

interface MobileTerritoriesListProps {
  formatGroupData?: FormatGroup[] | null;
  isLoading?: boolean;
}

const formatLabel = (format?: "hypermarket" | "market") => {
  if (format === "hypermarket") return "Hyper";
  if (format === "market") return "Super";
  return null;
};

const MobileTerritoriesList: React.FC<MobileTerritoriesListProps> = ({ formatGroupData, isLoading }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Shared click handler for row and button
  const handleRowClick = (id?: string) => {
    if (!id) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set("territoryId", id);
    navigate(`?${newParams.toString()}`);
  };

  // Extract and flat-map all TerritoryData (DTIs) from FormatGroups
  const territories = useMemo(() => {
    if (!formatGroupData) return [];
    return formatGroupData.flatMap((group) =>
      (group.territoryData || []).map((td) => ({ ...td, format: td.format ?? group.format }))
    );
  }, [formatGroupData]);

  // Filter territories dynamically by name
  const filteredTerritories = useMemo(() => {
    return territories.filter((td) =>
      (td.name || "").toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [territories, searchQuery]);

  if (isLoading) {
    return <LoadingCard />;
  }

  return (
    <Container>
      {/* Header Section (Fixed Top) */}
      <HeaderSection>

        {/* Search Field with magnifying glass icon inside */}
        <SearchInput
          label="Rechercher"
          hideLabel
          icon="search"
          name="territorySearch"
          type="text"
          placeholder="Rechercher"
          value={searchQuery}
          onChange={(e: any) => setSearchQuery(e.target.value)}
        />
      </HeaderSection>

      {/* Scrollable Territory List Container */}
      <ScrollableListContainer>
        {filteredTerritories.length === 0 ? (
          <NoResults>
            <EmptyText>Aucun résultat trouvé</EmptyText>
          </NoResults>
        ) : (
          filteredTerritories.map((td, index) => (
            <TerritoryRow
              key={td.id || String(index)}
              onClick={() => handleRowClick(td.id)}
            >
              <InfoArea>
                {/* Rank / Sequence Circular Badge */}
                <RankBadge>
                  {index + 1}
                </RankBadge>

                {/* Territory Information */}
                <DetailsContainer>
                  <NameRow>
                    <DetailsText>
                      {td.name}
                    </DetailsText>
                    {formatLabel(td.format) && (
                      <FormatTag>{formatLabel(td.format)}</FormatTag>
                    )}
                  </NameRow>
                  <DetailsText>
                    {formatKvalue(td.kpis?.caN ?? 0)}
                  </DetailsText>
                </DetailsContainer>
              </InfoArea>

              {/* Progress badge at the end of the line */}
              {td.kpis?.progCa !== undefined && (
                <BadgeContainer>
                  <DeltaBadge value={td.kpis.progCa} />
                </BadgeContainer>
              )}

              {/* Action Trigger - Navigation on Arrow Tap */}
              <ActionButton
                icon="chevron-right"
                variant="transparent"
                tone="neutral"
                onClick={(e: any) => {
                  e.stopPropagation();
                  handleRowClick(td.id);
                }}
              />
            </TerritoryRow>
          ))
        )}
      </ScrollableListContainer>
    </Container>
  );
};

export default MobileTerritoriesList;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  min-height: 0;
  background-color: #f8fafc;
`;

const HeaderSection = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchInput = styled(MrclInputText)`
  display: block;
  width: 100%;
`;

const ScrollableListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const NoResults = styled.div`
  padding: 32px;
  text-align: center;
`;

const EmptyText = styled(MrclText)`
  color: #64748b;
`;

const TerritoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: #f1f5f9;
  }
`;

const InfoArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const RankBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e2e8f0;
  color: #111212;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DetailsText = styled(MrclText)`
  font-weight: 700;
  font-size: 14px;
  color: #1e293b;
  text-align: left;
`;

const FormatTag = styled(MrclText)`
  font-weight: 400;
  font-size: 14px;
  color: #64748b;
`;

const BadgeContainer = styled.div`
  flex-shrink: 0;
  margin-right: 8px;
`;

const ActionButton = styled(customElement("mrcl-icon-button"))`
  background: none !important;
  color: #005CA9 !important;
  border: none !important;
`;